import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ShieldCheck, FileDown, Clock, Building2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicPack() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/public/pack/${token}/info`);
      setInfo(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "This link is not valid.");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const download = async () => {
    setDownloading(true);
    try {
      const resp = await axios.get(`${BACKEND_URL}/api/public/pack/${token}`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      // also download a copy
      const a = document.createElement("a");
      a.href = url;
      a.download = `audentra-${(info?.worker_name || "worker").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-cqc-pack.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // refresh use count
      load();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Could not open the pack.");
    } finally {
      setDownloading(false);
    }
  };

  const brandColor = info?.brand_primary_color || "#012E88";
  const expiryStr = info?.expires_at ? new Date(info.expires_at).toLocaleString() : null;

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-8">
          <div className="flex items-center gap-2">
            {info?.brand_logo_url ? (
              <img src={info.brand_logo_url} alt="logo" className="h-9 w-9 rounded-md object-cover" />
            ) : (
              <div className="h-9 w-9 rounded-md flex items-center justify-center" style={{ backgroundColor: brandColor }}>
                <ShieldCheck className="h-5 w-5 text-white" />
              </div>
            )}
            <div>
              <div className="font-heading text-lg font-bold tracking-tight">{info?.org_name || "Audentra"}</div>
              <div className="text-[0.65rem] tracking-[0.2em] uppercase text-slate-500">CQC Compliance Pack</div>
            </div>
          </div>

          {error && (
            <div className="mt-6 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" data-testid="share-error">
              <div className="font-semibold">This link can&apos;t be opened</div>
              <div className="mt-1">{error}</div>
            </div>
          )}

          {!error && !info && (
            <div className="mt-8 text-slate-500 text-sm">Loading share link…</div>
          )}

          {info && !error && (
            <>
              <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mt-6">
                Compliance pack for <span style={{ color: brandColor }}>{info.worker_name}</span>
              </h1>
              <div className="mt-2 text-slate-600 text-sm flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5" /> {info.worker_role} · {info.org_name}
              </div>
              {info.shared_by_name && (
                <div className="mt-1 text-slate-500 text-xs">Shared by {info.shared_by_name}</div>
              )}
              {info.note && (
                <div
                  data-testid="share-note-display"
                  className="mt-5 rounded-lg border-l-4 p-4 bg-slate-50 text-sm text-slate-800 whitespace-pre-wrap"
                  style={{ borderLeftColor: brandColor }}
                >
                  {info.note}
                </div>
              )}
              {expiryStr && (
                <div className="mt-4 text-slate-500 text-xs flex items-center gap-1.5">
                  <Clock className="h-3 w-3" /> This share link expires {expiryStr}
                  {info.max_uses ? ` · ${info.uses}/${info.max_uses} downloads` : ""}
                </div>
              )}
              <Button
                onClick={download}
                disabled={downloading}
                data-testid="public-pack-download"
                className="mt-8 w-full h-11 rounded-full text-white"
                style={{ backgroundColor: brandColor }}
              >
                <FileDown className="h-4 w-4 mr-1.5" /> {downloading ? "Opening…" : "Open compliance pack (PDF)"}
              </Button>
              <p className="mt-4 text-xs text-slate-500">
                This is a point-in-time export from Audentra Compliance. All views are audit-logged.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
